import { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useData } from '../../context/DataContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const PropertyModal = ({ visible, onCancel, property }) => {
  const { addProperty, updateProperty, mortgages } = useData();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    propertyType: 'residential',
    currentValue: 0,
    purchasePrice: 0,
    purchaseDate: '',
    annualGrowthRate: 3.5,
    linkedMortgageId: '',
    currency: 'GBP',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  useEscapeKey(onCancel, visible);

  const propertyTypes = [
    { id: 'residential', name: 'Residential' },
    { id: 'buy-to-let', name: 'Buy to Let' },
    { id: 'commercial', name: 'Commercial' },
  ];

  useEffect(() => {
    if (visible) {
      if (property) {
        setFormData({
          name: property.name || '',
          address: property.address || '',
          propertyType: property.propertyType || 'residential',
          currentValue: property.currentValue || 0,
          purchasePrice: property.purchasePrice || 0,
          purchaseDate: property.purchaseDate || '',
          annualGrowthRate: property.annualGrowthRate ?? 3.5,
          linkedMortgageId: property.linkedMortgageId || '',
          currency: property.currency || 'GBP',
          notes: property.notes || '',
        });
      } else {
        setFormData({
          name: '',
          address: '',
          propertyType: 'residential',
          currentValue: 0,
          purchasePrice: 0,
          purchaseDate: '',
          annualGrowthRate: 3.5,
          linkedMortgageId: '',
          currency: 'GBP',
          notes: '',
        });
      }
      setErrors({});
    }
  }, [visible, property]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter a property name';
    if (formData.currentValue <= 0) newErrors.currentValue = 'Please enter the current value';
    if (formData.annualGrowthRate < 0) newErrors.annualGrowthRate = 'Growth rate cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const data = {
      ...formData,
      currentValue: parseFloat(formData.currentValue) || 0,
      purchasePrice: parseFloat(formData.purchasePrice) || 0,
      annualGrowthRate: parseFloat(formData.annualGrowthRate) || 0,
    };

    if (property) {
      updateProperty(property.id, data);
    } else {
      addProperty(data);
    }
    onCancel();
  };

  // Available mortgages that aren't already linked to another property
  const availableMortgages = mortgages.filter(m =>
    !m.linkedPropertyId || m.linkedPropertyId === property?.id
  );

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-4">
                  {property ? 'Edit Property' : 'Add Property'}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Property Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Main Residence"
                      className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="e.g., 10 Downing Street, London"
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                      <select
                        value={formData.propertyType}
                        onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {propertyTypes.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Value <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        step="1000"
                        min="0"
                        value={formData.currentValue}
                        onChange={(e) => setFormData({ ...formData, currentValue: parseFloat(e.target.value) || 0 })}
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.currentValue ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.currentValue && <p className="mt-1 text-xs text-red-600">{errors.currentValue}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price</label>
                      <input
                        type="number"
                        step="1000"
                        min="0"
                        value={formData.purchasePrice}
                        onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) || 0 })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                      <input
                        type="date"
                        value={formData.purchaseDate}
                        onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annual Growth Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={formData.annualGrowthRate}
                        onChange={(e) => setFormData({ ...formData, annualGrowthRate: parseFloat(e.target.value) || 0 })}
                        className={`block w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.annualGrowthRate ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.annualGrowthRate && <p className="mt-1 text-xs text-red-600">{errors.annualGrowthRate}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Linked Mortgage</label>
                      <select
                        value={formData.linkedMortgageId}
                        onChange={(e) => setFormData({ ...formData, linkedMortgageId: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">None</option>
                        {availableMortgages.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Any additional notes..."
                    />
                  </div>

                  <div className="flex gap-3 justify-end pt-2">
                    <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                      style={{ backgroundColor: '#012E58' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                    >
                      {property ? 'Update Property' : 'Add Property'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PropertyModal;
